import { ArrowUpRight, BadgeCheck, Download, FileText, Share2 } from 'lucide-react'
import React, { useMemo, useState, type ReactNode } from 'react'
import { StorefrontProductTopGrid } from './storefrontProductPresentation'

export type StorefrontProductFieldRow = {
  field: {
    key: string
    label: string
    unit?: string
  }
  value: unknown
}

export type StorefrontProductDocument = {
  id?: string
  name: string
  originalName?: string
  url: string
  mimeType: string
  size: number
}

type ProductImageGalleryProps = {
  activeProductImage: string
  productGallery: string[]
  productName: string
  onSelectProductImage: (imageUrl: string) => void
  onOpenZoom?: () => void
  compact?: boolean
}

type ProductPurchaseCardProps = {
  formatCurrency: (value: number) => string
  priceExVat: number
  priceIncVat: number
  availabilityLabel: string
  availabilityTone: 'ok' | 'warn' | 'critical' | 'info'
  availabilityText: string
  cartQuantity: number
  disableQuantityControls: boolean
  disableAddToCart: boolean
  addToCartLabel: string
  addToCartMessage: string
  addToCartMessageTone: 'success' | 'error' | null
  onIncreaseCartQuantity: () => void
  onDecreaseCartQuantity: () => void
  onAddToCart: () => void
  onShareProduct?: () => void
  disableShareAction?: boolean
  compact?: boolean
}

type ProductDetailsPanelShellProps = {
  productName: string
  variantSelector?: ReactNode
  purchaseCard: ReactNode
  compact?: boolean
}

type ProductAttributesSectionProps = {
  title: string
  rows: StorefrontProductFieldRow[]
  description?: string
  renderValue?: (row: StorefrontProductFieldRow) => ReactNode
}

type ProductDocumentsSectionProps = {
  documents: StorefrontProductDocument[]
}

type ProductContentFrameProps = {
  children: ReactNode
  compact?: boolean
}

const ZOOM_SCALE = 2.1
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const renderScalarValue = (value: unknown) => {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'Si' : 'No'
  return String(value)
}

export const renderStorefrontProductFieldValue = (row: StorefrontProductFieldRow) => {
  const rendered = renderScalarValue(row.value)
  const unit = row.field.unit?.trim()
  if (!unit || !rendered.trim() || Array.isArray(row.value) || typeof row.value === 'boolean') return rendered
  return `${rendered} ${unit}`
}

const EmptyGalleryPlaceholder = () => (
  <div className="flex aspect-[16/8] max-h-[520px] min-h-[220px] w-full min-w-0 items-center justify-center overflow-hidden bg-white p-5 text-center sm:aspect-[16/7] sm:min-h-[240px] sm:p-6">
    <div className="max-w-xs min-w-0 space-y-3">
      <div className="mx-auto grid h-12 w-12 place-items-center border border-slate-200 bg-slate-50">
        <svg className="h-6 w-6 text-slate-500" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 7h14v10H5z" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 17v2m8-2v2M9 10h6m-6 3h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
      <div className="space-y-1">
        <p className="text-wrap text-sm font-semibold text-slate-900">Immagini in aggiornamento</p>
        <p className="text-wrap text-xs leading-relaxed text-slate-500">Le immagini prodotto saranno disponibili a breve.</p>
      </div>
    </div>
  </div>
)

export const StorefrontProductImageGallery = ({
  activeProductImage,
  productGallery,
  productName,
  onSelectProductImage,
  onOpenZoom,
  compact = false,
}: ProductImageGalleryProps) => {
  const [hovering, setHovering] = useState(false)
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 })

  const zoomedImageStyle = useMemo(
    () => ({
      transform: `scale(${hovering ? ZOOM_SCALE : 1})`,
      transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
    }),
    [hovering, zoomPosition.x, zoomPosition.y],
  )
  const hasMultipleImages = productGallery.length > 1

  return (
    <div className={`${hasMultipleImages && !compact ? 'grid gap-4 md:grid-cols-[76px_minmax(0,1fr)]' : ''} min-w-0`}>
      {hasMultipleImages && (
        <div className={`order-2 flex gap-2 overflow-x-auto pb-1 ${
          compact ? 'mt-3' : 'md:order-1 md:grid md:max-h-[680px] md:grid-cols-1 md:content-start md:gap-3 md:overflow-visible md:pb-0'
        }`}>
          {productGallery.map((imageUrl, index) => (
            <button
              key={imageUrl}
              type="button"
              onClick={() => onSelectProductImage(imageUrl)}
              className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-white transition ${
                activeProductImage === imageUrl
                  ? 'border-slate-950 ring-1 ring-slate-950'
                  : 'border-slate-200 hover:border-slate-400'
              }`}
              aria-label={`Mostra immagine ${index + 1} di ${productName}`}
            >
              <img
                src={imageUrl}
                alt={productName}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                className="h-full w-full object-contain p-1"
              />
            </button>
          ))}
        </div>
      )}

      <div className={`order-1 min-w-0 border-y border-slate-200 py-2 sm:py-3 ${compact ? '' : 'md:order-2'}`}>
        {activeProductImage ? (
          <button
            type="button"
            onClick={onOpenZoom}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            onMouseMove={(event) => {
              const bounds = event.currentTarget.getBoundingClientRect()
              const relativeX = ((event.clientX - bounds.left) / bounds.width) * 100
              const relativeY = ((event.clientY - bounds.top) / bounds.height) * 100
              setZoomPosition({
                x: clamp(relativeX, 0, 100),
                y: clamp(relativeY, 0, 100),
              })
            }}
            className={`group relative block aspect-[4/3] max-h-[680px] w-full min-w-0 overflow-hidden bg-white sm:aspect-[16/10] ${
              onOpenZoom ? (hovering ? 'cursor-crosshair' : 'cursor-zoom-in') : 'cursor-default'
            }`}
          >
            <img
              src={activeProductImage}
              alt={productName}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              sizes="(min-width: 1280px) 52vw, (min-width: 768px) 58vw, 100vw"
              className="h-full w-full object-contain transition-transform duration-150 ease-out will-change-transform"
              style={zoomedImageStyle}
            />
            <span className="pointer-events-none absolute left-3 top-3 hidden rounded-full border border-white/60 bg-slate-950/65 px-3 py-1 text-xs font-semibold text-white backdrop-blur sm:inline-flex">
              Muovi il mouse per zoom
            </span>
            {onOpenZoom ? (
              <span className="pointer-events-none absolute bottom-3 right-3 rounded-full border border-white/60 bg-slate-950/65 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                Fullscreen
              </span>
            ) : null}
            <span className="absolute inset-0 bg-slate-900/0 transition group-hover:bg-slate-900/5" />
          </button>
        ) : (
          <EmptyGalleryPlaceholder />
        )}
      </div>
    </div>
  )
}

export const StorefrontProductPurchaseCard = ({
  formatCurrency,
  priceExVat,
  priceIncVat,
  availabilityLabel,
  availabilityTone,
  availabilityText,
  cartQuantity,
  disableQuantityControls,
  disableAddToCart,
  addToCartLabel,
  addToCartMessage,
  addToCartMessageTone,
  onIncreaseCartQuantity,
  onDecreaseCartQuantity,
  onAddToCart,
  onShareProduct,
  disableShareAction = false,
  compact = false,
}: ProductPurchaseCardProps) => {
  const availabilityToneClass =
    availabilityTone === 'critical'
      ? 'bg-rose-500'
      : availabilityTone === 'warn'
      ? 'bg-amber-500'
      : availabilityTone === 'ok'
      ? 'bg-emerald-500'
      : 'bg-cyan-500'

  return (
    <section aria-label="Acquisto prodotto" className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3 border-t border-slate-200 pt-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prezzo</p>
          <p className={`${compact ? 'text-3xl' : 'text-4xl'} mt-1 font-black tracking-tight text-slate-900`}>
            {formatCurrency(priceIncVat)}
          </p>
        </div>
        {onShareProduct ? (
          <button
            type="button"
            onClick={onShareProduct}
            disabled={disableShareAction}
            className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 transition hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
            title="Condividi prodotto"
            aria-label="Condividi prodotto"
          >
            <Share2 className="h-4.5 w-4.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="flex items-start gap-3 border-y border-slate-200 py-4">
        <span className={`mt-1 inline-block h-2.5 w-2.5 rounded-full ${availabilityToneClass}`} aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-slate-900">{availabilityLabel}</p>
          <p className="text-xs leading-relaxed text-slate-600">{availabilityText}</p>
        </div>
      </div>

      <div className={`grid gap-3 ${compact ? '' : 'sm:grid-cols-[auto_minmax(0,1fr)] sm:items-stretch'}`}>
        <div
          className="inline-flex h-12 min-w-0 items-center justify-between rounded-lg border border-slate-300 bg-white px-1"
          aria-label="Selettore quantità"
        >
          <button
            type="button"
            onClick={onDecreaseCartQuantity}
            disabled={disableQuantityControls || cartQuantity <= 1}
            className="grid h-10 w-10 place-items-center rounded-md text-xl font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Diminuisci quantità"
          >
            -
          </button>
          <span className="min-w-10 text-center text-base font-bold text-slate-900" aria-live="polite">
            {cartQuantity}
          </span>
          <button
            type="button"
            onClick={onIncreaseCartQuantity}
            disabled={disableQuantityControls}
            className="grid h-10 w-10 place-items-center rounded-md text-xl font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Aumenta quantità"
          >
            +
          </button>
        </div>
        <button
          type="button"
          onClick={onAddToCart}
          disabled={disableAddToCart}
          className="inline-flex h-12 items-center justify-center rounded-lg border border-emerald-300 bg-emerald-600 px-5 text-sm font-semibold text-white shadow-[0_14px_28px_-18px_rgba(5,150,105,0.7)] transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {addToCartLabel}
        </button>
      </div>

      {addToCartMessage ? (
        <p className={`mt-3 text-sm ${addToCartMessageTone === 'error' ? 'text-rose-700' : 'text-emerald-700'}`}>
          {addToCartMessage}
        </p>
      ) : null}
    </section>
  )
}

export const StorefrontProductDetailsPanelShell = ({
  productName,
  variantSelector,
  purchaseCard,
  compact = false,
}: ProductDetailsPanelShellProps) => (
  <div className={compact ? '' : 'lg:sticky lg:top-24'}>
    <section className={`space-y-5 border-y border-slate-200 py-5 ${
      compact ? '' : 'lg:border-y-0 lg:border-l lg:py-0 lg:pl-8'
    }`}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <span className="inline-flex items-center gap-1.5 text-slate-700">
            <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
            Scheda prodotto
          </span>
        </div>
        <h1 className={`${compact ? 'text-3xl' : 'text-3xl lg:text-4xl'} break-words font-black leading-tight text-slate-950`}>
          {productName}
        </h1>
      </div>

      {variantSelector}
      {purchaseCard}
    </section>
  </div>
)

export const StorefrontProductAttributesSection = ({
  title,
  rows,
  description,
  renderValue,
}: ProductAttributesSectionProps) => {
  if (rows.length === 0) return null

  return (
    <section>
      <header className="mb-4">
        <h3 className="text-lg font-black tracking-tight text-slate-950">{title}</h3>
        {description ? <p className="text-sm text-slate-600">{description}</p> : null}
      </header>
      <dl className="grid border-t border-slate-200 text-sm md:grid-cols-2 md:gap-x-14">
        {rows.map((row) => (
          <div
            key={row.field.key}
            className="grid gap-2 border-b border-slate-200 py-4 sm:grid-cols-[minmax(150px,0.44fr)_minmax(0,1fr)] sm:gap-5"
          >
            <dt className="text-sm font-bold text-slate-700">{row.field.label}</dt>
            <dd className="text-base font-black text-slate-950 md:text-[15px]">
              {renderValue ? renderValue(row) : renderStorefrontProductFieldValue(row)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

const formatFileSize = (size: number) => {
  if (!Number.isFinite(size) || size <= 0) return ''
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const formatDocumentType = (document: StorefrontProductDocument) => {
  const extension = document.name.split('.').pop()?.toUpperCase() ?? ''
  return extension || document.mimeType || 'FILE'
}

const isPdfDocument = (document: StorefrontProductDocument) =>
  document.mimeType.toLowerCase() === 'application/pdf' ||
  /\.pdf($|\?)/i.test(document.url) ||
  /\.pdf$/i.test(document.name)

export const StorefrontProductDocumentsSection = ({ documents }: ProductDocumentsSectionProps) => {
  const seen = new Set<string>()
  const renderedDocuments = documents.filter((document) => {
    if (!document.url || seen.has(document.url)) return false
    seen.add(document.url)
    return true
  })

  if (renderedDocuments.length === 0) return null

  const documentCountLabel = `${renderedDocuments.length} ${
    renderedDocuments.length === 1 ? 'documento' : 'documenti'
  }`

  return (
    <section className="border-t border-slate-200 pt-7" aria-labelledby="product-documents-title">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Documentazione tecnica</p>
          <h2 id="product-documents-title" className="mt-1 text-2xl font-black tracking-tight text-slate-950">
            Documenti
          </h2>
        </div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{documentCountLabel}</p>
      </header>
      <div className="border-t border-slate-200">
        {renderedDocuments.map((document) => {
          const size = formatFileSize(document.size)
          const isPdf = isPdfDocument(document)
          const actionLabel = isPdf ? 'Apri PDF' : 'Scarica'
          return (
            <div
              key={document.url}
              className="flex flex-col gap-3 border-b border-slate-200 py-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <a
                href={document.url}
                target={isPdf ? '_blank' : undefined}
                rel={isPdf ? 'noreferrer' : undefined}
                download={isPdf ? undefined : true}
                className="group flex min-w-0 items-start gap-3"
              >
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 transition group-hover:border-slate-300 group-hover:text-slate-700">
                  <FileText className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-950 transition group-hover:text-slate-700">
                    {document.name}
                  </p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {[formatDocumentType(document), size].filter(Boolean).join(' • ')}
                  </p>
                </div>
              </a>
              <a
                href={document.url}
                target={isPdf ? '_blank' : undefined}
                rel={isPdf ? 'noreferrer' : undefined}
                download={isPdf ? undefined : true}
                className="inline-flex items-center gap-1 self-start text-xs font-semibold text-slate-600 transition hover:text-slate-900 sm:self-center"
              >
                {actionLabel}
                {isPdf ? (
                  <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Download className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </a>
            </div>
          )
        })}
      </div>
    </section>
  )
}

export const StorefrontProductContentFrame = ({ children, compact = false }: ProductContentFrameProps) => (
  <div
    style={{
      backgroundColor: 'var(--theme-color-background, #ffffff)',
      color: 'var(--theme-color-text, #0f172a)',
    }}
  >
    <div className={`w-full max-w-none space-y-10 ${
      compact ? 'px-4 pb-7 pt-6' : 'px-4 pb-8 pt-8 sm:px-6 lg:px-10 lg:pb-10 2xl:px-14'
    }`}>
      {children}
    </div>
  </div>
)

export { StorefrontProductTopGrid }
